import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import SplitLeg, { RememberAccount } from './SplitLeg';
import DraftEditDialog from './DraftEditDialog';
import { EDIT_REF_KEY } from '../CompanyWorkspace/JournalBook';
import { AttachmentsBtn } from '../CompanyWorkspace/attachments';   // owner 2026-11-02: a draft's supporting files, for a draft the full-screen editor (which holds them too) is not offered on

// ── Draft journal entries ───────────────────────────────────────────────────────────────────────
// Entries derived from the VAT sub-ledger, waiting for a person. Nothing reaches the statutory
// books from here without someone posting it, which is the point: the machine can tell what a
// document is, but not always which expense account it belongs to, and a guess that posts itself is
// worse than a blank waiting to be filled.
// (EXCEPTION, owner 2026-11-02, "Automatically post accounting entries", ruled limited: a client may turn on automatic posting of its
// sale and purchase tax lines, server/src/services/autoPost.ts. A line it left behind is here as any draft is, and `autoHold` says why
// on its card. So a blank is still never guessed: a line on a fallback account is exactly what it leaves behind.)
//
// A purchase draft arrives on the account recorded for that SUPPLIER (services/vendorAccounts.ts,
// answered once on the Supplier → account panel). Where no answer exists yet it still parks on 5900
// "other expenses" and says so on the draft, because a guess that posts itself is worse than a
// blank waiting to be filled — picking the right account IS the review.
//
// Some documents are not one account at all: half a bill is this and half is that. Any leg of a
// draft can therefore be DIVIDED across several accounts by hand (./SplitLeg), the parts show as the
// one leg they came from, and the division can be undone. Nothing about it posts anything, and
// nothing about it is remembered for the next document unless somebody says so — which is a button
// of its own, for the reason written at the top of SplitLeg.tsx.
//
// ⛔ A read-only seat (a client's own books login, the auditor — utils/readOnlySeat.ts) reads the
// drafts and is offered nothing that writes: no Generate / Refresh stale, no Post / Post all /
// Discard, no Split / Undo split / Remember, and each leg's account as text instead of a select that
// saves on change. The server refuses every one of those to that login, and each refusal pages the
// admins; the list, the stale warnings and the notes stay exactly as staff see them.
//
// ⛔ A REVERSAL draft (W1 ship) is the entry it reverses with the sides swapped, and posting it releases that entry's
// source (services/journalPost.ts reversalMirrorRefusal). So on one — `reverses` set by GET /journal/drafts — each leg's
// account reads as text and there is no Split, for every seat: the server refuses both (409 reversal_locked), and a
// request bound to be refused is a defect. One whose lines are no longer that mirror (changed before this rule) carries
// the post door's own refusal in `mirror`: it is said on the draft, in the reader's language, and the draft is offered
// no Post and left out of Post all — Discard is the way out the sentence names.
//
// ⛔ (Batch 12 tail T4, COA-D11) WHAT may change on a draft is the server's one verdict, `edit` (services/draftEdit.ts
// draftEditOf), read on every row: 'full' (a manual draft) — the account select on every leg and an Edit button that opens
// the full-screen editor (Bookkeeping/DraftEditDialog.tsx: header and every line in one PUT) — no Split: the editor divides
// lines; 'review' (a generated VAT-line draft) — the select and Split on its FREE legs only (`lines[].free`: not the party's,
// a VAT or a cash line), the select listing only the accounts such a leg may take (the account list's `free`), and Edit;
// none — no select, no Split, no Edit, and, where no other line on the card already says why, one plain sentence
// (EDIT_REF_KEY). A stale draft is offered no Post and is left out of Post all (W4); its select and Split are gone too —
// the doors answer 409 stale. Undo split stays wherever a split stands (it only restores).

type Line = { id: string; line_no: number; account_id: string; dr: number; cr: number; code: string; name_th: string; name_en: string; partner_id: string; split_id: string;
  /** (Batch 12 tail T4) A leg a generated draft may move (services/draftEdit.ts legFree); the party it carries. */
  free?: boolean; partner_name?: string };
/** (Batch 12 tail T4) The draft's edit verdict (services/draftEdit.ts draftEditOf). */
type EditVerdict = { scope: 'full' | 'review' | null; code: string | null; message: string };
type Split = { id: string; side: string; satang: number; parts: number; code: string; name_th: string; name_en: string; by: string; at: number };
/** `stale`: the document this draft was derived from has moved since (phase 5). The post door
 *  refuses it; this says so first, and says what to do — a derived draft is re-derived by
 *  discarding it and generating again. */
type Stale = { reason: 'line' | 'items' | 'both' | 'gone' | 'vouchers' | 'prepaid' | 'prepaid_wait' | string; changed_at: number; message: string;
  /** (Batch 11 W4 P9, W3 lows) The words beyond the reason: the vouchers by number, the withholding's case. */
  words?: { key: string; params: Record<string, string> } };
/** `reverses`: the entry a reversal draft takes back out — and `locked`, what the server answers a change of its
 *  accounts or legs (reversalLineRefusal: the way to book it differently, by where the reversed entry came from), the
 *  note's hover. `mirror`: the post door's refusal of a reversal draft that is no longer that entry's line-by-line
 *  mirror (code reversal_not_mirror, the two numbers, the server's sentence). */
type Reverses = { entryId: string; docNo: string; locked?: string;
  /** (Batch 11 W4 P9, LB-13) The way out's route and its figures: the hover is worded by pa_draft_reversal_way_<code> in the
   *  reader's language, the server's English (`locked`) its fallback. */
  lockedCode?: string; lockedParams?: Record<string, string> | null };
type Mirror = { code: string; docNo: string; reverses: string; message: string };
/** `hand` (W1 ship verification): the post door's refusal of a draft for a source recorded BY HAND
 *  (journalPost.handPostRefusal) — a manual entry made for a source that no longer stands (hand_source_gone) or that
 *  something else books now (hand_source_held), or an entry derived from a source such a manual entry holds
 *  (recorded_by_hand). `kind` / `label` name the source, `by` the manual entry. Said on the draft in place of Post. */
type Hand = { code: string; message: string; kind: string; label: string; by: string };
/** `handChanged` (W1 ship): a manual entry made for a source by hand, still a draft, whose source no longer says what it
 *  said when the draft was made (services/handBooks.ts). Not a refusal: the card's own Post stays, the card says to check
 *  the figures. ⛔ Left out of Post all (W1 ship review): posting takes the source's print anew, so a draft posted unseen
 *  in a batch would book the stale figures with nothing ever saying so again — the server's batch door refuses it too
 *  (journalPost.postMany, code hand_changed), and a request bound to be refused is a defect. */
type HandChanged = { kind: string; label: string; fields: string[] };
type Draft = { id: string; entry_date: string; source_book: string; doc_no: string; narration: string; calc_note: string; lines: Line[]; splits: Split[]; stale?: Stale | null;
  reverses?: Reverses | null; mirror?: Mirror | null; hand?: Hand | null; handChanged?: HandChanged | null;
  /** (Batch 11 W4 P9, LB-18) A manual entry made for a source by hand: which one (every such draft, refused or not). */
  madeFor?: { kind: string; id: string; label: string } | null;
  /** (Batch 12 tail T4) What may be edited on it — the selects, Split and Edit follow it. */
  edit?: EditVerdict | null;
  /** (Owner 2026-11-02, services/autoPost.ts) Why automatic posting left this one for a person; null for every other draft. */
  autoHold?: { code: string; message: string; savedBy: string; at: number } | null };
/** How a by-hand refusal is said, by its code — each key written in full so a grep finds it; a code this build does not
 *  know is said in the server's words. */
const HAND_KEY: Record<string, string> = {
  hand_source_gone: 'pa_draft_hand_source_gone', hand_source_held: 'pa_draft_hand_source_held', recorded_by_hand: 'pa_draft_recorded_by_hand',
  hand_prepaid_taken: 'pa_draft_hand_prepaid_taken',   // W3.1: it books a receipt's withholding another entry books already
};
const HAND_FOR_KEY: Record<string, string> = {
  tax_line: 'pa_manual_for_tax_line', bank_line: 'pa_manual_for_bank_line', cash_line: 'pa_manual_for_cash_line',
};
type Account = { id: string; code: string; name_th: string; name_en: string; class: string; free?: boolean };
/** (Batch 12 tail T4) The verdicts whose reason no other line on the card says (stale, by-hand, mirror and reversal have
 *  their own): said in one plain sentence where the select, Split and Edit are not offered. */
const SAY_WHY = new Set(['generated_settlement', 'generated_register', 'no_free_leg', 'unknown_origin', 'payroll', 'period_closed']);
/** May this leg's account be chosen here, by the draft's verdict? */
const canPick = (d: Draft, l: Line) => d.edit?.scope === 'full' || (d.edit?.scope === 'review' && !!l.free);

// The short statutory journal names (เงินสด / รายวันซื้อ …) live in the translation table so the
// label survives a locale switch unchanged — they are the names the server prints, not English that
// happens to be Thai. Keys only here: t() is a hook and cannot be called at module scope.
const BOOK_KEY: Record<string, string> = { cash: 'pa_jnl_cash', bank: 'pa_jnl_bank', purchase: 'pa_jnl_purchase', sale: 'pa_jnl_sale', general: 'pa_jnl_general' };
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (satang: number) => money(Number(satang || 0) / 100);

/** The legs of one draft, with the pieces of a hand-made split kept together under the split they
 *  came from. Parts are contiguous because the server renumbers an entry's legs after every split,
 *  so this only has to notice where one run ends. */
function groupsOf(d: Draft): { splitId: string; split: Split | null; lines: Line[] }[] {
  const out: { splitId: string; split: Split | null; lines: Line[] }[] = [];
  for (const l of d.lines) {
    const id = l.split_id || '';
    const last = out[out.length - 1];
    if (last && last.splitId === id) last.lines.push(l);
    else out.push({ splitId: id, split: id ? (d.splits || []).find(s => s.id === id) || null : null, lines: [l] });
  }
  return out;
}

/** Which memory a charge on this entry would be remembered in. Only the sale and purchase books
 *  have one: a cash, bank or general entry is not a document with charges on it, and offering to
 *  "remember" one would promise something no reader exists for. */
const chargeSide = (book: string): 'sale' | 'purchase' | null =>
  book === 'sale' ? 'sale' : book === 'purchase' ? 'purchase' : null;

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};

export default function DraftEntries({ clientId, periodId, periodClosed, onPosted, refreshKey = 0 }:
  { clientId: string; periodId: string; periodClosed: boolean; onPosted: () => void; refreshKey?: number }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  // What may be changed here at all: an open period, and a seat that writes.
  const writable = !periodClosed && !readOnly;
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  // One leg at a time, in each case: two open split editors on one entry would each be pinned to a
  // figure the other is changing.
  const [splitting, setSplitting] = useState('');
  const [remembering, setRemembering] = useState('');
  // (Batch 12 tail T4) The draft open in the full-screen editor ('' = none).
  const [editing, setEditing] = useState('');

  // refreshKey is bumped by the panel above when it creates a draft — an adjustment, a reversal or
  // a swept-in accrual. Without it a new draft sits on the server, invisible until the page reloads.
  const load = useCallback(async () => {
    try {
      const d: any = await api.jnlDrafts(clientId, periodId);
      setDrafts(d.entries || []);
    } catch (e: any) { setErr(e.message); setDrafts([]); }
  }, [clientId, periodId, refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setOpen(false); setMsg(''); setErr(''); load(); }, [load]);
  const staleN = (drafts || []).filter(d => !!d.stale).length;
  useEffect(() => { api.jnlAccounts(clientId).then((d: any) => setAccounts(d.accounts || [])).catch(() => setAccounts([])); }, [clientId]);

  const generate = async () => {
    setBusy('gen'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlGenerate(clientId, periodId);
      // Counted, not inflected: zh and th have no plural, so each count carries its own label.
      const bits = [t('pa_draft_gen_created', { n: r.created })];
      if (r.skipped) bits.push(t('pa_draft_gen_skipped', { n: r.skipped }));
      if (r.needsAccount) bits.push(t('pa_draft_gen_needs_account', { n: r.needsAccount }));
      // (W1 ship) Lines a manual entry made for them by hand holds: Generate leaves them to that entry — named, so nobody
      // reads "nothing happened" as "not in the books" and books them again by hand.
      if (r.byHand?.length) {
        const list = r.byHand.slice(0, 4).map((b: any) => `${b.docNo || t('pa_draft_no_doc_no')} → ${b.entryNo}`).join('; ') +
          (r.byHand.length > 4 ? t('pa_draft_gen_blocked_more', { n: r.byHand.length - 4 }) : '');
        bits.push(t('pa_draft_gen_by_hand', { n: r.byHand.length, list }));
      }
      setMsg(bits.join(' · '));
      // Refusals are listed rather than counted: a line the generator would not touch is the one
      // thing a person actually has to go and look at. b.why is the server's own words — unwrapped.
      if (r.blocked?.length) {
        const list = r.blocked.slice(0, 4).map((b: any) => `${b.docNo || t('pa_draft_no_doc_no')}: ${b.why}`).join('; ') +
          (r.blocked.length > 4 ? t('pa_draft_gen_blocked_more', { n: r.blocked.length - 4 }) : '');
        setErr(t('pa_draft_gen_blocked', { n: r.blocked.length, list }));
      }
      if (r.created) setOpen(true);
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // Re-derive the stale ones nobody has worked on. The ones somebody HAS worked on come back
  // listed — they are discarded by hand or not at all, because rebuilding one would throw away a
  // divided leg, a chosen account or linked paperwork without asking.
  const refreshStale = async () => {
    setBusy('stale'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlRefreshStale(clientId, periodId);
      // ⛔ Every number the server distinguishes is said separately. `rebuilt` is what the button
      // did; `notRebuilt` is the drafts it put back because the generator would not derive their
      // document again (they are still stale, still warned, nothing lost); `createdOther` is the
      // drafts the same pass made for documents that never had one. Reporting the discards as
      // "rebuilt", as this once did, claimed work that may not have happened.
      const bits = [t('pa_draft_stale_rebuilt', { n: Number(r.rebuilt) || 0 })];
      if (r.notRebuilt?.length) {
        bits.push(t('pa_draft_stale_not_rebuilt', {
          n: r.notRebuilt.length,
          list: r.notRebuilt.slice(0, 3).map((x: any) => x.why).join('; '),
        }));
      }
      if (Number(r.withdrawn) > 0) bits.push(t('pa_draft_stale_withdrawn', { n: Number(r.withdrawn) }));
      if (r.kept?.length) bits.push(t('pa_draft_stale_kept', { n: r.kept.length }));
      if (Number(r.createdOther) > 0) bits.push(t('pa_draft_stale_other', { n: Number(r.createdOther) }));
      setMsg(bits.join(' · '));
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const setAccount = async (lineId: string, accountId: string) => {
    setErr('');
    try { await api.jnlSetLineAccount(lineId, accountId); await load(); }
    catch (e: any) { setErr(e.message); }
  };

  const post = async (ids: string[], label: string) => {
    setBusy(label); setErr(''); setMsg('');
    try {
      const r: any = ids.length === 1 ? await api.jnlPost(ids[0]) : await api.jnlPostMany(ids);
      // A hand draft whose source changed after this list was read (the batch door's hand_changed) is said in the
      // reader's language; every other failure in the server's words, as before.
      const noOf = (id: string) => (drafts || []).find(d => d.id === id)?.doc_no || '';
      // (Batch 11 W4 P9, LB-28) One posted meanwhile by someone else (`already`) is done, not failed.
      const done = (Number(r.posted) || 0) + (Array.isArray(r.already) ? r.already.length : 0);
      if (r.failed?.length) setErr(r.failed.map((f: any) => f.code === 'hand_changed' && noOf(f.id) ? t('pa_draft_post_many_hand_changed', { docNo: noOf(f.id) }) : f.why).join('; '));
      else setMsg(ids.length === 1 ? t('pa_draft_posted_single') : t('pa_draft_posted_n', { n: done }));
      await load(); onPosted();
    } catch (e: any) {
      // (Batch 11 W4 P9, LB-28) Posted meanwhile (the door's already_posted, naming who and when): it is done — the list is
      // read again and no error is shown.
      if (e?.payload?.code === 'already_posted') { setMsg(t('pa_draft_posted_single')); await load(); onPosted(); }
      else setErr(e.message);
    } finally { setBusy(''); }
  };

  // Putting a split back. Confirmed first because it discards the rows somebody typed, and the
  // confirmation names the account the money goes back onto so nobody undoes the wrong one.
  const undoSplit = async (sp: Split) => {
    if (!window.confirm(t('pa_split_undo_confirm', { account: `${sp.code} ${sp.name_en}` }))) return;
    setBusy(sp.id); setErr(''); setMsg('');
    try { await api.jnlUndoSplit(sp.id); setMsg(t('pa_split_undone')); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // A reversal draft's discard says what discarding it leaves: the entry it reverses stays in the books until it is
  // reversed again (nothing is generated) — the way out pa_draft_mirror_broken names.
  const discard = async (id: string, docNo: string, reverses = '') => {
    if (!window.confirm(reverses ? t('pa_draft_discard_confirm_reversal', { docNo, reverses }) : t('pa_draft_discard_confirm', { docNo }))) return;
    setBusy(id); setErr('');
    try { await api.jnlDiscard(id); await load(); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (drafts === null) return null;
  const n = drafts.length;
  // What Post all sends: every draft but one the post door would refuse — a reversal that is no mirror (`mirror`), a
  // draft for a source recorded by hand (`hand`) — nor a hand draft whose source changed since it was made
  // (`handChanged`): only its own card posts it, where the warning is read (the batch door refuses it, hand_changed).
  // (Batch 11 W4 P9, W3.1 low) …nor a STALE one: the post door refuses it (400 stale), so Post all leaves it out and its own
  // card offers no Post — it says what to do instead (discard and generate again, or post its carrier first).
  const postable = drafts.filter(d => !d.mirror && !d.hand && !d.handChanged && !d.stale);
  const handLeft = drafts.filter(d => !d.mirror && !d.hand && !!d.handChanged).length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-file-pencil" style={{ fontSize: 18, color: n ? '#B45309' : 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            {t('pa_draft_title')}
            {n > 0 && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', padding: '1px 8px', borderRadius: 999 }}>{n}</span>}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {n ? t('pa_draft_sub_pending') : t('pa_draft_sub_empty')}
          </span>
        </span>
        {/* Offered only when something IS stale: a button that always answers "nothing to do" is a
            button nobody reads. staleN counts the drafts whose document has moved since. */}
        {writable && staleN > 0 && (
          <button onClick={refreshStale} disabled={!!busy} title={t('pa_draft_stale_btn_title')}
                  style={{ ...btn, opacity: busy ? 0.6 : 1, color: '#B42318', borderColor: '#FECACA' }}>
            <i className={busy === 'stale' ? 'ti ti-loader' : 'ti ti-refresh'} style={{ display: 'inline-block', animation: busy === 'stale' ? 'spin 0.7s linear infinite' : undefined }} aria-hidden />
            {' '}{t('pa_draft_stale_btn', { n: staleN })}
          </button>
        )}
        {writable && (
          <button onClick={generate} disabled={!!busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
            <i className={busy === 'gen' ? 'ti ti-loader' : 'ti ti-wand'} style={{ display: 'inline-block', animation: busy === 'gen' ? 'spin 0.7s linear infinite' : undefined }} aria-hidden />
            {' '}{t('pa_draft_generate')}
          </button>
        )}
        {n > 0 && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_draft_hide') : t('pa_draft_review')}</button>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {open && n > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px' }}>
          {!readOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              {/* Why Post all counts fewer than the list holds: the hand drafts whose source changed are posted one by
                  one, from their own cards. */}
              {handLeft > 0 && (
                <span data-post-all-left={handLeft} style={{ fontSize: 11.5, color: '#92400E', minWidth: 0, flex: '1 1 260px', textAlign: 'right' }}>
                  <i className="ti ti-alert-triangle" aria-hidden /> {t('pa_draft_post_all_hand_left', { n: handLeft })}
                </span>
              )}
              <button onClick={() => post(postable.map(d => d.id), 'all')} disabled={!!busy || periodClosed || !postable.length}
                style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: busy ? 0.6 : 1 }}>
                <i className={busy === 'all' ? 'ti ti-loader' : 'ti ti-check'} aria-hidden /> {t('pa_draft_post_all', { n: postable.length })}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {drafts.map(d => (
              <div key={d.id} style={{ border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px', background: 'var(--bg-app)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' }}>{d.doc_no}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{dmy(d.entry_date)} · {t(BOOK_KEY[d.source_book] || d.source_book)}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.narration}</span>
                  {(readOnly || periodClosed || !d.edit?.scope) && <AttachmentsBtn clientId={clientId} kind="journal_entry" id={d.id} docNo={d.doc_no} />}
                  {!readOnly && (<>
                    {/* (Batch 12 tail T4) Edit, where the verdict lets anything be changed — the full-screen editor. */}
                    {!periodClosed && !!d.edit?.scope && (
                      <button onClick={() => { setEditing(d.id); setSplitting(''); setRemembering(''); }} disabled={!!busy} data-draft-edit-btn={d.edit.scope}
                        style={{ ...btn, padding: '5px 10px', fontSize: 11.5 }}><i className="ti ti-pencil" aria-hidden /> {t('pa_draft_edit')}</button>
                    )}
                    {!d.mirror && !d.hand && !d.stale && (
                      <button onClick={() => post([d.id], d.id)} disabled={!!busy || periodClosed} style={{ ...btn, padding: '5px 10px', fontSize: 11.5 }}>{t('pa_draft_post')}</button>
                    )}
                    <button onClick={() => discard(d.id, d.doc_no, d.reverses?.docNo || '')} disabled={!!busy} title={t('pa_draft_discard_title')}
                      style={{ ...btn, padding: '5px 9px', fontSize: 11.5, color: '#B42318' }}><i className="ti ti-trash" aria-hidden /></button>
                  </>)}
                </div>
                {/* Why automatic posting left this one here (owner 2026-11-02). Amber: nothing is wrong with it, a person is wanted. The server's own sentence is the hover. */}
                {d.autoHold && (
                  <div data-draft-auto-hold={d.autoHold.code} title={d.autoHold.message}
                    style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', padding: '6px 11px', borderBottom: '1px solid #FDE68A', fontWeight: 600 }}>
                    <i className="ti ti-bolt-off" aria-hidden />{' '}
                    {t('aw_autopost_left', { why: t(`aw_autopost_hold_${d.autoHold.code}`, { defaultValue: d.autoHold.message }), who: d.autoHold.savedBy || '—' })}
                  </div>
                )}
                {/* Louder than calc_note on purpose: a note explains how this draft was built, this
                    says it should not be posted at all until it is rebuilt. */}
                {d.stale && (
                  <div title={d.stale.message} data-draft-stale={d.stale.reason} style={{ fontSize: 11.5, color: '#B42318', background: '#FEF2F2', padding: '6px 11px', borderBottom: '1px solid #FECACA', fontWeight: 600 }}>
                    {/* The server sends both a reason CODE and an English sentence. The screen
                        says it in the operator's own language from the code, and falls back to the
                        server's words for any reason this build does not know yet — a reason
                        nobody can read is a warning nobody acts on. */}
                    <i className="ti ti-alert-triangle" aria-hidden />{' '}
                    {d.stale.words
                      ? t(d.stale.words.key, { ...d.stale.words.params, defaultValue: t(`pa_draft_stale_r_${d.stale.reason}`, { defaultValue: d.stale.message }) })
                      : t(`pa_draft_stale_r_${d.stale.reason}`, { defaultValue: d.stale.message })}
                  </div>
                )}
                {/* A reversal that no longer mirrors what it reverses: the post door's refusal, before anyone presses
                    Post (there is none on it). Worded from the code; the server's sentence for a reason this build
                    does not know. */}
                {d.mirror && (
                  <div style={{ fontSize: 11.5, color: '#B42318', background: '#FEF2F2', padding: '6px 11px', borderBottom: '1px solid #FECACA', fontWeight: 600 }}>
                    <i className="ti ti-alert-triangle" aria-hidden />{' '}
                    {d.mirror.code === 'reversal_not_mirror'
                      ? t('pa_draft_mirror_broken', { docNo: d.mirror.docNo, reverses: d.mirror.reverses })
                      : d.mirror.message}
                  </div>
                )}
                {/* A draft for a source recorded by hand that the post door refuses (W1 ship verification): why, in the
                    reader's language, the server's sentence as the hover — and no Post, only Discard. */}
                {d.hand && (
                  <div data-hand-refused={d.hand.code} title={d.hand.message}
                    style={{ fontSize: 11.5, color: '#B42318', background: '#FEF2F2', padding: '6px 11px', borderBottom: '1px solid #FECACA', fontWeight: 600 }}>
                    <i className="ti ti-alert-triangle" aria-hidden />{' '}
                    {HAND_KEY[d.hand.code]
                      ? t(HAND_KEY[d.hand.code], { docNo: d.doc_no, by: d.hand.by,
                        what: HAND_FOR_KEY[d.hand.kind] ? t(HAND_FOR_KEY[d.hand.kind], { label: d.hand.label }) : d.hand.label })
                      : d.hand.message}
                  </div>
                )}
                {/* A draft made for a source by hand whose source has changed since (W1 ship): said, with what to do — and
                    Post stays (the person checks the figures; nothing is refused or corrected). Amber, not red. */}
                {d.handChanged && !d.hand && (
                  <div data-hand-changed={d.handChanged.kind}
                    style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', padding: '6px 11px', borderBottom: '1px solid #FDE68A', fontWeight: 600 }}>
                    <i className="ti ti-alert-triangle" aria-hidden />{' '}
                    {t('pa_draft_hand_changed', { docNo: d.doc_no,
                      what: HAND_FOR_KEY[d.handChanged.kind] ? t(HAND_FOR_KEY[d.handChanged.kind], { label: d.handChanged.label }) : d.handChanged.label })}
                  </div>
                )}
                {/* Why a reversal's accounts read as text and it has no Split — and, as its hover, the server's way to
                    book it differently (never an entry of one's own beside a source Generate derives again). */}
                {d.madeFor && !d.hand && (
                  <div data-draft-made-for={d.madeFor.kind} style={{ fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-app)', padding: '6px 11px', borderBottom: '1px solid var(--border)' }}>
                    <i className="ti ti-hand-finger" aria-hidden /> {t('pa_draft_made_for', {
                      what: HAND_FOR_KEY[d.madeFor.kind] ? t(HAND_FOR_KEY[d.madeFor.kind], { label: d.madeFor.label }) : d.madeFor.label })}
                  </div>
                )}
                {!!d.reverses && !d.mirror && (
                  <div title={d.reverses.locked
                    ? (d.reverses.lockedCode ? t(`pa_draft_reversal_way_${d.reverses.lockedCode}`, { ...(d.reverses.lockedParams || {}), defaultValue: d.reverses.locked }) : d.reverses.locked)
                    : undefined}
                    data-reversal-way={d.reverses.locked ? (d.reverses.lockedCode || '1') : undefined}
                    style={{ fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-app)', padding: '6px 11px', borderBottom: '1px solid var(--border)' }}>
                    <i className="ti ti-arrow-back-up" aria-hidden /> {t('pa_draft_reversal_note', { reverses: d.reverses.docNo })}
                    {d.reverses.locked ? <> {t('pa_draft_reversal_hover')}</> : null}
                  </div>
                )}
                {/* (Batch 12 tail T4) Why nothing on it is chosen or split here — said only where no line above says it. */}
                {writable && !!d.edit && !d.edit.scope && SAY_WHY.has(String(d.edit.code || '')) && (
                  <div data-draft-edit-why={d.edit.code || ''} title={d.edit.message}
                    style={{ fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-app)', padding: '6px 11px', borderBottom: '1px solid var(--border)' }}>
                    <i className="ti ti-lock" aria-hidden /> {t(EDIT_REF_KEY[String(d.edit.code)] || 'aw_jl_edit_ref_unknown_origin', { defaultValue: d.edit.message })}
                  </div>
                )}
                {d.calc_note && (
                  <div style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', padding: '6px 11px', borderBottom: '1px solid var(--border)' }}>
                    <i className="ti ti-info-circle" aria-hidden /> {d.calc_note}
                  </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <tbody>
                    {groupsOf(d).map(g => (
                      <React.Fragment key={g.splitId || g.lines[0].id}>
                        {/* A divided leg is shown AS one leg divided. Three credits that happen to add
                            up read as three unrelated legs, and the person reviewing the entry has to
                            be able to see that somebody allocated them by hand — and undo it. */}
                        {!!g.split && (
                          <tr style={{ borderTop: '1px solid var(--border)', background: '#FFFBEB' }}>
                            <td colSpan={4} style={{ padding: '5px 11px', fontSize: 11, color: '#92400E' }}>
                              <i className="ti ti-arrows-split-2" aria-hidden />{' '}
                              {t('pa_split_group', { count: g.split.parts })} ·{' '}
                              {t('pa_split_group_of', { amount: baht(g.split.satang), account: `${g.split.code} ${g.split.name_en}` })}
                              {!!g.split.by && ` · ${t('pa_split_by', { name: g.split.by })}`}
                              {writable && (
                                <button onClick={() => undoSplit(g.split!)} disabled={!!busy}
                                  style={{ ...btn, marginLeft: 9, padding: '2px 8px', fontSize: 11 }}>{t('pa_split_undo')}</button>
                              )}
                            </td>
                          </tr>
                        )}
                        {g.lines.map(l => (
                          <React.Fragment key={l.id}>
                            <tr style={{ borderTop: '1px solid var(--border)' }}>
                              <td style={{ padding: '6px 11px', width: '48%',
                                           borderLeft: g.split ? '3px solid #F1C232' : '3px solid transparent' }}>
                                {/* The account is editable on a draft and only on a draft — a posted entry
                                    is corrected by reversing it, never by quietly changing what it said.
                                    A read-only seat reads it as text: the select saves on change. */}
                                {readOnly || !!d.reverses || !canPick(d, l) ? (
                                  <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>{l.code ? `${l.code} · ${l.name_th} — ${l.name_en}` : '—'}</span>
                                ) : (
                                  <select value={l.account_id} onChange={e => setAccount(l.id, e.target.value)} disabled={periodClosed}
                                    style={{ width: '100%', maxWidth: 380, padding: '4px 7px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                                    {/* A generated draft's free leg takes only an account such a leg may go to (the door's own test). */}
                                    {accounts.filter(a => d.edit?.scope !== 'review' || a.free || a.id === l.account_id)
                                      .map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th} — {a.name_en}</option>)}
                                  </select>
                                )}
                                {l.partner_id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>{l.partner_id}</span>}
                              </td>
                              <td style={{ padding: '6px 11px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{l.dr ? baht(l.dr) : ''}</td>
                              <td style={{ padding: '6px 11px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{l.cr ? baht(l.cr) : ''}</td>
                              <td style={{ padding: '4px 9px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {writable && !d.reverses && d.edit?.scope === 'review' && !!l.free && (
                                  <button onClick={() => { setSplitting(splitting === l.id ? '' : l.id); setRemembering(''); }}
                                    title={t('pa_split_btn_title')} style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>
                                    <i className="ti ti-arrows-split-2" aria-hidden /> {t('pa_split_btn')}
                                  </button>
                                )}
                                {writable && !!chargeSide(d.source_book) && (
                                  <button onClick={() => { setRemembering(remembering === l.id ? '' : l.id); setSplitting(''); }}
                                    style={{ ...btn, padding: '3px 8px', fontSize: 11, marginLeft: 5, color: 'var(--text-secondary)' }}>
                                    <i className="ti ti-bookmark" aria-hidden /> {t('pa_rem_link')}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {splitting === l.id && !readOnly && !d.reverses && d.edit?.scope === 'review' && !!l.free && (
                              <tr><td colSpan={4} style={{ padding: '0 11px 10px' }}>
                                <SplitLeg line={l} accounts={accounts.filter(a => a.free)}
                                  onSaved={async (m: string) => { setSplitting(''); setErr(''); setMsg(m); await load(); }}
                                  onCancel={() => setSplitting('')} />
                              </td></tr>
                            )}
                            {remembering === l.id && !readOnly && !!chargeSide(d.source_book) && (
                              <tr><td colSpan={4} style={{ padding: '0 11px 10px' }}>
                                <RememberAccount entryId={d.id} clientId={clientId} side={chargeSide(d.source_book)!}
                                  account={{ code: l.code, name_en: l.name_en }} onClose={() => setRemembering('')} />
                              </td></tr>
                            )}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* (Batch 12 tail T4) The full-screen editor of one draft, by its verdict. A save closes it and reads the list again. */}
      {editing && (
        <DraftEditDialog entryId={editing} onClose={() => setEditing('')}
          onSaved={async (m: string) => { setEditing(''); setErr(''); setMsg(m); await load(); onPosted(); }}
          onChanged={() => { load(); }} />
      )}
    </div>
  );
}
